/**
 * The ⓘ — the detail a screen leaves out to stay clear, one tap (or click)
 * away: a popover beside what it explains, closed by Escape or a tap outside.
 * The trigger is a real button with a name ("About “Days away from Portugal”").
 *
 * Presentation only: the label and the content are the caller's, translated.
 */
import { Info } from 'lucide-react';
import type { ReactNode } from 'react';

import { cn } from '../lib/utils';
import { Button } from '../primitives/button';
import { Popover, PopoverContent, PopoverTrigger } from '../primitives/popover';

export interface InfoPopoverProps {
  /** What the ⓘ explains, for assistive technology. */
  label: string;
  children: ReactNode;
  className?: string;
  'data-testid'?: string;
}

export function InfoPopover({
  label,
  children,
  className,
  'data-testid': testId,
}: InfoPopoverProps) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          className={cn('h-6 w-6 shrink-0 rounded-pill text-muted-foreground', className)}
          aria-label={label}
          data-testid={testId}
        >
          <Info aria-hidden="true" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-80 space-y-3 text-sm"
        data-testid={testId ? `${testId}-content` : undefined}
      >
        {children}
      </PopoverContent>
    </Popover>
  );
}
